import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme, alpha, type Theme } from '@mui/material/styles';
import { Add, DeleteOutline, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import {
  updateSieveNest,
  addSieveNestSize,
  updateSieveNestSize,
  deleteSieveNestSize,
} from '../../../services/sieveNestService';
import type { SieveNest, SieveNestSize } from '../../../types/sieveNest';

interface Props {
  nest: SieveNest;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

interface SizeRowDraft {
  key?: string;          // SieveNestSizeKey if persisted
  sieveSizeMm: string;   // string for typing
  designation: string;
  displayName: string;
  sortOrder: number;
  isNew?: boolean;
  isDeleted?: boolean;
}

function fromSize(s: SieveNestSize): SizeRowDraft {
  return {
    key: s.SieveNestSizeKey,
    sieveSizeMm: String(s.SieveSizeMm),
    designation: s.SieveDesignation ?? '',
    displayName: s.SieveDisplayName ?? '',
    sortOrder: s.SortOrder,
  };
}

const inputSx = {
  '& .MuiInputBase-input': { py: 0.4, px: 0.75, fontSize: '0.78rem' },
};

export const SieveNestEditDialog = ({ nest, onClose, onSaved }: Props) => {
  const theme = useTheme();
  const [name, setName] = useState(nest.NestName);
  const [description, setDescription] = useState(nest.Description ?? '');
  const [rows, setRows] = useState<SizeRowDraft[]>(
    nest.sizes.slice().sort((a, b) => a.SortOrder - b.SortOrder).map(fromSize),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(nest.NestName);
    setDescription(nest.Description ?? '');
    setRows(nest.sizes.slice().sort((a, b) => a.SortOrder - b.SortOrder).map(fromSize));
  }, [nest]);

  const setRow = (idx: number, patch: Partial<SizeRowDraft>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const moveRow = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= rows.length) return;
    const swap = rows.slice();
    [swap[idx], swap[target]] = [swap[target], swap[idx]];
    setRows(swap.map((r, i) => ({ ...r, sortOrder: i + 1 })));
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { sieveSizeMm: '', designation: '', displayName: '', sortOrder: prev.length + 1, isNew: true },
    ]);
  };

  const removeRow = (idx: number) => {
    const r = rows[idx];
    if (r.isNew) {
      setRows((prev) => prev.filter((_, i) => i !== idx));
    } else {
      setRow(idx, { isDeleted: true });
    }
  };

  const visibleRows = rows
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => !r.isDeleted);

  const handleSave = async () => {
    setError(null);
    if (!name.trim()) { setError('Nest name is required'); return; }
    for (const { r } of visibleRows) {
      if (!r.sieveSizeMm.trim() || Number.isNaN(Number(r.sieveSizeMm))) {
        setError('Every size row needs a numeric Size (mm) value');
        return;
      }
    }
    setSaving(true);
    try {
      // 1. Update nest header if changed
      if (name.trim() !== nest.NestName || (description.trim() || null) !== (nest.Description ?? null)) {
        await updateSieveNest(nest.SieveNestKey, name.trim(), description.trim() || null);
      }
      // 2. Apply size deltas — sequentially to keep things simple
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        if (r.isDeleted && r.key) {
          await deleteSieveNestSize(nest.SieveNestKey, r.key);
        } else if (r.isNew && !r.isDeleted) {
          await addSieveNestSize(nest.SieveNestKey, {
            sieveSizeMm: Number(r.sieveSizeMm),
            sieveDesignation: r.designation || undefined,
            sieveDisplayName: r.displayName || undefined,
            sortOrder: r.sortOrder,
          });
        } else if (!r.isDeleted && r.key) {
          // Update existing — always, in case sortOrder changed
          await updateSieveNestSize(nest.SieveNestKey, r.key, {
            sieveSizeMm: Number(r.sieveSizeMm),
            sieveDesignation: r.designation || undefined,
            sieveDisplayName: r.displayName || undefined,
            sortOrder: r.sortOrder,
          });
        }
      }
      await onSaved();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string; message?: string } } };
      setError(e?.response?.data?.message || e?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit "{nest.NestName}"</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 1.5, mb: 2 }}>
          <TextField
            label="Nest Name" size="small" fullWidth
            value={name} onChange={(e) => setName(e.target.value)}
            sx={inputSx}
          />
          <TextField
            label="Description" size="small" fullWidth
            value={description} onChange={(e) => setDescription(e.target.value)}
            sx={inputSx}
          />
        </Box>

        <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.7rem' }}>
          Sieve Sizes
        </Typography>

        <Box sx={{
          mt: 1,
          display: 'grid',
          gridTemplateColumns: '60px 110px 110px 110px 90px',
          gap: 0.75,
          alignItems: 'center',
        }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>#</Typography>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>Size (mm)</Typography>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>Designation</Typography>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>Display Name</Typography>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>Actions</Typography>

          {visibleRows.map(({ r, i }, displayIdx) => (
            <RowFragment
              key={r.key ?? `new-${i}`}
              displayIdx={displayIdx + 1}
              row={r}
              onChange={(patch) => setRow(i, patch)}
              onMoveUp={displayIdx > 0 ? () => moveRow(i, -1) : undefined}
              onMoveDown={displayIdx < visibleRows.length - 1 ? () => moveRow(i, 1) : undefined}
              onRemove={() => removeRow(i)}
              theme={theme}
            />
          ))}
        </Box>

        <Button startIcon={<Add />} onClick={addRow} sx={{ mt: 1.5, textTransform: 'none' }} size="small">
          Add Sieve Size
        </Button>

        {error && (
          <Typography color="error" sx={{ mt: 2, fontSize: '0.8rem' }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RowFragment = ({
  displayIdx,
  row,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
  theme,
}: {
  displayIdx: number;
  row: SizeRowDraft;
  onChange: (patch: Partial<SizeRowDraft>) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onRemove: () => void;
  theme: Theme;
}) => (
  <>
    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{displayIdx}</Typography>
    <TextField
      type="number" size="small" value={row.sieveSizeMm}
      onChange={(e) => onChange({ sieveSizeMm: e.target.value })}
      sx={inputSx}
    />
    <TextField
      size="small" value={row.designation}
      onChange={(e) => onChange({ designation: e.target.value })}
      placeholder='e.g. #4 or 3"'
      sx={inputSx}
    />
    <TextField
      size="small" value={row.displayName}
      onChange={(e) => onChange({ displayName: e.target.value })}
      placeholder="(optional)"
      sx={inputSx}
    />
    <Box sx={{ display: 'flex', gap: 0.25 }}>
      <Tooltip title="Move up">
        <span>
          <IconButton size="small" onClick={onMoveUp} disabled={!onMoveUp}>
            <ArrowUpward sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Move down">
        <span>
          <IconButton size="small" onClick={onMoveDown} disabled={!onMoveDown}>
            <ArrowDownward sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Remove">
        <IconButton size="small" color="error" onClick={onRemove}
          sx={{ ml: 'auto', '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.08) } }}>
          <DeleteOutline sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  </>
);
